
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BookingCompletionData } from "@/components/booking/BookingCompletionDialog";
import { format } from "date-fns";

export const useBookingCompletion = (serviceId: string | undefined, fetchBookings: () => Promise<void>) => {
  const { toast } = useToast();
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [progressValue, setProgressValue] = useState(0);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const handleCompleteBooking = async (bookingId: number, completionData: BookingCompletionData): Promise<boolean> => {
    try {
      // Set the booking ID for which an action is in progress
      setActionInProgress(bookingId);
      setProgressValue(15); // Start with initial progress

      // Get the booking data
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('customer_id, service_id, start_time, end_time')
        .eq('id', bookingId)
        .single();
      
      if (bookingError) throw bookingError;
      
      setProgressValue(30);

      // Get the authenticated user's ID (vendor)
      const { data: { session } } = await supabase.auth.getSession();
      const vendorId = session?.user.id;
      
      if (!vendorId) {
        throw new Error("Authentication required");
      }
      
      setProgressValue(50);
      
      // Create a record in the booking_completions table
      const { error: completionError } = await supabase
        .from('booking_completions')
        .insert({
          booking_id: bookingId,
          payment_amount: completionData.paymentAmount,
          rating: completionData.rating,
          comment: completionData.comment || null,
          vendor_id: vendorId,
          customer_id: bookingData.customer_id
        });
      
      if (completionError) throw completionError;
      
      setProgressValue(70);
      
      // Update the booking status to completed
      const { error: updateError } = await supabase
        .from('bookings')
        .update({ status: 'completed' })
        .eq('id', bookingId);

      if (updateError) throw updateError;
      
      setProgressValue(90);

      // Try to send notifications (non-critical)
      try {
        // Format dates for notification
        const startDateTime = new Date(bookingData.start_time);
        const endDateTime = new Date(bookingData.end_time);
        const formattedDate = format(startDateTime, "EEEE, MMMM d, yyyy");
        const formattedTime = `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`;
        
        // Send notification to admin about completion
        await supabase.functions.invoke('send-notification', {
          body: {
            type: 'booking_completed',
            serviceId: bookingData.service_id,
            bookingId: bookingId,
            completionData: {
              ...completionData,
              date: formattedDate,
              time: formattedTime
            }
          }
        }).catch(err => console.log('Non-critical error sending notification:', err));
      } catch (notificationError) {
        // Log but don't throw - notifications are non-critical
        console.error('Error sending notifications:', notificationError);
      }

      // Refresh bookings data
      await fetchBookings();
      
      setProgressValue(100);
      setShowSuccessAnimation(true);
      
      // After all operations are complete, show success toast and reset state
      setTimeout(() => {
        toast({
          title: "Success",
          description: "Booking marked as completed successfully",
          variant: "success",
        });
        
        setShowSuccessAnimation(false);
        
        // Add a small delay before clearing the in-progress state to allow animations to complete
        setTimeout(() => {
          setActionInProgress(null);
          setProgressValue(0);
        }, 500);
      }, 1500);
      
      return true;
    } catch (error: any) {
      console.error('Error completing booking:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to complete booking",
      });
      
      // Reset states on error
      setProgressValue(0);
      setActionInProgress(null);
      return false;
    }
  };

  return {
    handleCompleteBooking,
    actionInProgress,
    progressValue,
    showSuccessAnimation
  };
};
